import { CalendarDays, ChevronLeft, ChevronRight } from 'lucide-react';
import { useLayoutEffect, useRef, type ReactNode } from 'react';
import { cn } from '@/shared/lib/cn';
import { DAY_RANGE } from './heuteProblem';
import { addCalendarDays, calendarWeek } from './operationsDate';

interface Props {
  date: string;
  today: string;
  onDateChange: (date: string) => void;
  /** Weitere Knöpfe neben „Heute“ und Kalender, z. B. Aktualisieren. */
  extra?: ReactNode;
}

const fullDate = new Intl.DateTimeFormat('de-DE', {
  weekday: 'long',
  day: 'numeric',
  month: 'long',
  year: 'numeric',
  timeZone: 'UTC',
});
const weekday = new Intl.DateTimeFormat('de-DE', { weekday: 'short', timeZone: 'UTC' });
const dayOf = (value: string) => new Date(`${value}T00:00:00Z`);

/**
 * Datumsband im Kopf (Board „Heute“): die Woche des gewählten Tages, der gewählte Tag doppelt so
 * breit mit großer Zahl. Pfeile gehen tageweise, dazu Kalender und „Heute“ – Datum bleibt in der URL.
 * Weiter als ein Jahr reicht das Band nicht: so weit liefert der Server keinen Tag.
 */
export function HeuteDateBand({ date, today, onDateChange, extra }: Props) {
  const min = addCalendarDays(today, -DAY_RANGE);
  const max = addCalendarDays(today, DAY_RANGE);
  const days = useRef<HTMLElement | null>(null);
  // Am Handy passt die Woche nicht ganz: der gewählte Tag rückt in die Mitte, ohne die Seite zu scrollen.
  useLayoutEffect(() => {
    const strip = days.current;
    const selected = strip?.querySelector<HTMLElement>('[aria-current="date"]');
    if (!strip || !selected) return;
    strip.scrollLeft = selected.offsetLeft - (strip.clientWidth - selected.clientWidth) / 2;
  }, [date]);
  const arrow =
    'flex w-10 flex-none items-center justify-center rounded-lg text-white hover:bg-white/10 disabled:opacity-40';
  return (
    <section
      aria-label="Datumsnavigation"
      className="rounded-tile flex w-full min-w-0 flex-col gap-1.5 border border-white/15 bg-white/[0.06] p-1.5 md:w-auto md:flex-row md:items-stretch md:gap-1"
    >
      <div className="flex min-w-0 items-stretch gap-1">
        <button
          type="button"
          aria-label="Vorheriger Tag"
          onClick={() => onDateChange(addCalendarDays(date, -1))}
          disabled={date <= min}
          className={arrow}
        >
          <ChevronLeft size={18} aria-hidden />
        </button>
        <nav
          ref={days}
          aria-label="Tage wählen"
          className="scrollbar-none relative flex min-w-0 flex-1 gap-1 overflow-x-auto"
        >
          {calendarWeek(date).map((day) => (
            <DayButton
              key={day}
              day={day}
              selected={day === date}
              today={day === today}
              disabled={day < min || day > max}
              onPick={() => onDateChange(day)}
            />
          ))}
        </nav>
        <button
          type="button"
          aria-label="Nächster Tag"
          onClick={() => onDateChange(addCalendarDays(date, 1))}
          disabled={date >= max}
          className={arrow}
        >
          <ChevronRight size={18} aria-hidden />
        </button>
      </div>
      <span aria-hidden className="mx-1 hidden w-px self-stretch bg-white/15 md:block" />
      <div className="flex items-center gap-1.5 px-1 md:pr-1">
        <button
          type="button"
          onClick={() => onDateChange(today)}
          className="text-forest-deep rounded-pill h-9 bg-white px-3.5 text-[13px] font-bold"
        >
          Heute
        </button>
        <label className="rounded-pill focus-within:outline-mint relative flex size-9 items-center justify-center border border-white/15 text-white focus-within:outline focus-within:outline-2 focus-within:outline-offset-2">
          <CalendarDays size={16} aria-hidden />
          <input
            type="date"
            value={date}
            min={min}
            max={max}
            onChange={(event) => event.target.value && onDateChange(event.target.value)}
            className="absolute inset-0 cursor-pointer opacity-0"
            aria-label="Datum wählen"
          />
        </label>
        {extra}
      </div>
    </section>
  );
}

function DayButton({
  day,
  selected,
  today,
  disabled,
  onPick,
}: {
  day: string;
  selected: boolean;
  today: boolean;
  disabled: boolean;
  onPick: () => void;
}) {
  const name = `${fullDate.format(dayOf(day))}${today ? ', Heute' : ''}${selected ? ', Ausgewählt' : ''}`;
  return (
    <button
      type="button"
      aria-current={selected ? 'date' : undefined}
      aria-label={name}
      onClick={onPick}
      disabled={disabled}
      className={cn(
        'flex h-[76px] flex-none flex-col items-center justify-center gap-1 rounded-lg disabled:opacity-40',
        selected
          ? 'text-forest-deep w-[84px] bg-white shadow-lg'
          : 'w-12 text-white hover:bg-white/10',
      )}
    >
      <span className="text-[11px] font-bold uppercase tracking-[0.08em]">
        {weekday.format(dayOf(day)).replace('.', '')}
      </span>
      <span
        className={cn(
          'font-serif font-bold leading-none tracking-[-0.03em]',
          selected ? 'text-[36px]' : 'text-lg opacity-85',
        )}
      >
        {dayOf(day).getUTCDate()}
      </span>
    </button>
  );
}
